import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Checkbox } from 'components/ui/checkbox'
import { Badge } from 'components/ui/badge'
import {
	initializeTemplates,
	initializeTemplateFinancialLineItems,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateFinancialLineItem,
	deleteTemplateFinancialLineItem,
} from 'slices/templateSlice'
import { GripVertical, Plus, Trash2, LayoutTemplate, Loader2, BarChart3 } from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Types ──────────────────────────────────────────────────────────────────────

interface LineItemData {
	_id: string
	year: number
	label: string
	category: string
	defaultAmount: number
	notes?: string
	isNew?: boolean
}

interface ROIDefaults {
	discountRate: number // decimal: 0.10 = 10%
	holdPeriod: number // years
	saleCapRate: number // decimal: 0.055 = 5.5%
	revenueGrowthRate: number // decimal: 0.05 = 5%
}

const DEFAULT_DEFAULTS: ROIDefaults = {
	discountRate: 0.1,
	holdPeriod: 5,
	saleCapRate: 0.055,
	revenueGrowthRate: 0.05,
}

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	item: LineItemData
	onYearChange: (id: string, value: number) => void
	onLabelChange: (id: string, value: string) => void
	onCategoryChange: (id: string, value: string) => void
	onAmountChange: (id: string, value: number) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	item,
	onYearChange,
	onLabelChange,
	onCategoryChange,
	onAmountChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_60px_1fr_140px_140px_32px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${
				isDragging ? 'bg-blue-50 shadow-md rounded' : 'hover:bg-gray-50'
			}`}
		>
			<div
				{...attributes}
				{...listeners}
				className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			<Input
				type="number"
				min={0}
				step={1}
				value={Number.isFinite(item.year) ? item.year : 0}
				onChange={e => onYearChange(id, parseInt(e.target.value, 10) || 0)}
				className="h-7 text-xs text-center"
				placeholder="0"
				title="Year (0 = initial capital, 1-N = annual flows)"
			/>

			<Input
				value={item.label}
				onChange={e => onLabelChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Line item (e.g. Year 1 Operating Cash Flow)"
			/>

			<Input
				type="number"
				value={Number.isFinite(item.defaultAmount) ? item.defaultAmount : 0}
				onChange={e => onAmountChange(id, parseFloat(e.target.value) || 0)}
				className="h-7 text-xs text-right"
				placeholder="0"
				title="Negative for outflows (e.g. initial capital), positive for inflows"
			/>

			<Input
				value={item.category}
				onChange={e => onCategoryChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Category (e.g. Investment, Operating, Exit)"
			/>

			<div className="flex items-center justify-center">
				<Checkbox
					checked={isSelected}
					onCheckedChange={checked => onSelect(id, !!checked)}
				/>
			</div>

			<button
				type="button"
				title="Delete line item"
				onClick={() => onDelete(id)}
				className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const ROIAnalysisTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector(
		(state: any) => state.templates,
	)
	const {
		entities: itemEntities,
		ids: itemIds,
		isLoading: itemsLoading,
	} = useSelector((state: any) => state.templates.financialLineItems)

	const roiTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'roi')
			.sort((a: any, b: any) =>
				(a.templateName || '').localeCompare(b.templateName || ''),
			)
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [defaults, setDefaults] = useState<ROIDefaults>(DEFAULT_DEFAULTS)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [itemOrder, setItemOrder] = useState<string[]>([])
	const [itemsMap, setItemsMap] = useState<Record<string, LineItemData>>({})
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalItemIds, setOriginalItemIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateFinancialLineItems(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		// Hydrate ROI assumptions from Template.defaults (Mixed). Fall back to
		// DEFAULT_DEFAULTS for any missing field so older templates render
		// cleanly.
		const tplDefaults = template.defaults || {}
		setDefaults({
			discountRate:
				typeof tplDefaults.discountRate === 'number'
					? tplDefaults.discountRate
					: DEFAULT_DEFAULTS.discountRate,
			holdPeriod:
				typeof tplDefaults.holdPeriod === 'number'
					? tplDefaults.holdPeriod
					: DEFAULT_DEFAULTS.holdPeriod,
			saleCapRate:
				typeof tplDefaults.saleCapRate === 'number'
					? tplDefaults.saleCapRate
					: DEFAULT_DEFAULTS.saleCapRate,
			revenueGrowthRate:
				typeof tplDefaults.revenueGrowthRate === 'number'
					? tplDefaults.revenueGrowthRate
					: DEFAULT_DEFAULTS.revenueGrowthRate,
		})

		const loaded = (itemIds || [])
			.map((id: string) => itemEntities[id])
			.filter(Boolean)
			// Sort by year first (so the projection reads chronologically), then
			// by seq within a year — multiple rows can share a year (e.g. Y5
			// Operating + Y5 Terminal Value).
			.sort((a: any, b: any) => {
				const ay = typeof a.year === 'number' ? a.year : 0
				const by = typeof b.year === 'number' ? b.year : 0
				if (ay !== by) return ay - by
				return (a.seq || 0) - (b.seq || 0)
			})

		const order: string[] = []
		const map: Record<string, LineItemData> = {}
		const origIds = new Set<string>()
		loaded.forEach((it: any) => {
			order.push(it._id)
			map[it._id] = {
				_id: it._id,
				year: typeof it.year === 'number' ? it.year : 0,
				label: it.label || '',
				category: it.category || '',
				defaultAmount: typeof it.defaultAmount === 'number' ? it.defaultAmount : 0,
				notes: it.notes || '',
			}
			origIds.add(it._id)
		})
		setItemOrder(order)
		setItemsMap(map)
		setOriginalItemIds(origIds)
		setSelectedItems(new Set())
		setErrors({})
		setIsDirty(false)
	}, [itemIds, itemEntities, selectedTemplateId, isCreateMode, templateEntities])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setItemOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Row Handlers ───────────────────────────────────────────────────────────

	const handleYearChange = (id: string, value: number) => {
		setItemsMap(prev => ({ ...prev, [id]: { ...prev[id], year: value } }))
		setIsDirty(true)
	}

	const handleLabelChange = (id: string, value: string) => {
		setItemsMap(prev => ({ ...prev, [id]: { ...prev[id], label: value } }))
		setIsDirty(true)
	}

	const handleCategoryChange = (id: string, value: string) => {
		setItemsMap(prev => ({ ...prev, [id]: { ...prev[id], category: value } }))
		setIsDirty(true)
	}

	const handleAmountChange = (id: string, value: number) => {
		setItemsMap(prev => ({ ...prev, [id]: { ...prev[id], defaultAmount: value } }))
		setIsDirty(true)
	}

	const handleSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedItems)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedItems(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedItems(checked ? new Set(itemOrder) : new Set())
	}

	const isAllSelected =
		itemOrder.length > 0 && itemOrder.every(id => selectedItems.has(id))

	const handleDeleteItem = (id: string) => {
		const item = itemsMap[id]
		const label = item?.label?.trim() || 'this line item'
		notify({
			title: 'Delete Line Item',
			text: `Are you sure you want to delete "${label}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setItemOrder(prev => prev.filter(iid => iid !== id))
				setItemsMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedItems(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedItems.size === 0) return
		notify({
			title: 'Delete Selected Line Items',
			text: `Are you sure you want to delete ${selectedItems.size} line item${
				selectedItems.size > 1 ? 's' : ''
			}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setItemOrder(prev => prev.filter(id => !selectedItems.has(id)))
				setItemsMap(prev => {
					const next = { ...prev }
					selectedItems.forEach(id => delete next[id])
					return next
				})
				setSelectedItems(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddItem = () => {
		const tempId = `_new_${nextTempId.current++}`
		// New rows default to year = (highest existing year + 1) so building a
		// projection in chronological order is natural. First row defaults to 0
		// (initial capital).
		const highestYear = itemOrder.reduce(
			(max, id) => Math.max(max, itemsMap[id]?.year ?? 0),
			-1,
		)
		const newItem: LineItemData = {
			_id: tempId,
			year: highestYear < 0 ? 0 : highestYear + 1,
			label: '',
			category: '',
			defaultAmount: 0,
			isNew: true,
		}
		setItemOrder(prev => [...prev, tempId])
		setItemsMap(prev => ({ ...prev, [tempId]: newItem }))
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setDefaults(DEFAULT_DEFAULTS)
			setItemOrder([])
			setItemsMap({})
			setOriginalItemIds(new Set())
			setSelectedItems(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate],
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate],
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyItem = itemOrder.find(id => !itemsMap[id]?.label.trim())
		if (emptyItem) newErrors.items = 'All line items must have a label'
		if (!(defaults.holdPeriod > 0))
			newErrors.defaults = 'Hold period must be greater than 0'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			const defaultsPayload = {
				discountRate: defaults.discountRate,
				holdPeriod: defaults.holdPeriod,
				saleCapRate: defaults.saleCapRate,
				revenueGrowthRate: defaults.revenueGrowthRate,
			}

			if (isCreateMode) {
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'roi',
						accessLevel,
						defaults: defaultsPayload,
					}),
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				for (let i = 0; i < itemOrder.length; i++) {
					const it = itemsMap[itemOrder[i]]
					await (dispatch as any)(
						createTemplateFinancialLineItem({
							templateId: newTemplateId,
							label: it.label.trim(),
							category: it.category.trim() || null,
							defaultAmount: it.defaultAmount,
							year: it.year,
							notes: it.notes || null,
							seq: i + 1,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
						defaults: defaultsPayload,
					}),
				)

				for (const origId of originalItemIds) {
					await (dispatch as any)(
						deleteTemplateFinancialLineItem({
							templateId: selectedTemplateId,
							_id: origId,
						}),
					)
				}
				for (let i = 0; i < itemOrder.length; i++) {
					const it = itemsMap[itemOrder[i]]
					await (dispatch as any)(
						createTemplateFinancialLineItem({
							templateId: selectedTemplateId,
							label: it.label.trim(),
							category: it.category.trim() || null,
							defaultAmount: it.defaultAmount,
							year: it.year,
							notes: it.notes || null,
							seq: i + 1,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateFinancialLineItems(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Derived ────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled =
		!templateName.trim() || !description.trim() || itemOrder.length === 0 || isSaving

	const netReturn = useMemo(
		() => itemOrder.reduce((sum, id) => sum + (itemsMap[id]?.defaultAmount || 0), 0),
		[itemOrder, itemsMap],
	)

	const formatCurrency = (n: number) => {
		const abs = Math.abs(n)
		const sign = n < 0 ? '-' : ''
		if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
		if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}k`
		return `${sign}$${abs}`
	}

	const discountRatePct = Math.round(defaults.discountRate * 10000) / 100
	const saleCapRatePct = Math.round(defaults.saleCapRate * 10000) / 100
	const revenueGrowthRatePct = Math.round(defaults.revenueGrowthRate * 10000) / 100

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">
							ROI Analysis Templates
						</h2>

						<div className="max-w-[1180px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{roiTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : roiTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">
											No templates yet
										</div>
									) : (
										roiTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">
															{template.description}
														</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button
										variant="ghost"
										onClick={handleNewTemplate}
										className="w-full justify-start"
									>
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<LayoutTemplate className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName)
															setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${
														errors.templateName ? 'border-red-500' : ''
													}`}
													placeholder="e.g. Standard 5-year ROI"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.templateName}
													</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description)
															setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${
														errors.description ? 'border-red-500' : ''
													}`}
													placeholder="What kind of ROI scenario is this for? Hold horizon, exit assumptions, etc."
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.description}
													</p>
												)}
											</div>
										</div>

										{/* Access Level + Net Return */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs items-center">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private'
														? 'Private'
														: level === 'org-wide'
															? 'Org-Wide'
															: 'Public'}
												</label>
											))}
											<div className="flex-1" />
											<span className="text-gray-600">
												{itemOrder.length} cash flows · undiscounted net
											</span>
											<Badge
												className={`text-[10px] ${
													netReturn > 0
														? 'bg-emerald-100 text-emerald-700'
														: netReturn < 0
															? 'bg-red-100 text-red-700'
															: 'bg-gray-200 text-gray-700'
												}`}
											>
												{formatCurrency(netReturn)}
											</Badge>
										</div>

										{/* Defaults / Assumptions */}
										<div className="mb-3 p-2 bg-blue-50 rounded text-xs flex items-center gap-3 flex-wrap">
											<span className="font-semibold text-gray-700 flex items-center gap-1">
												<BarChart3 className="w-3.5 h-3.5 text-[#0080fe]" />
												Assumptions
											</span>
											<label className="flex items-center gap-1.5">
												<span className="text-gray-600">Discount rate</span>
												<div className="relative">
													<Input
														type="number"
														step={0.1}
														value={discountRatePct}
														onChange={e => {
															const pct = parseFloat(e.target.value)
															setDefaults(d => ({
																...d,
																discountRate: Number.isFinite(pct) ? pct / 100 : 0,
															}))
															setIsDirty(true)
														}}
														className="h-6 w-16 text-xs text-right pr-5"
													/>
													<span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">
														%
													</span>
												</div>
											</label>
											<label className="flex items-center gap-1.5">
												<span className="text-gray-600">Hold period</span>
												<Input
													type="number"
													min={1}
													step={1}
													value={defaults.holdPeriod}
													onChange={e => {
														setDefaults(d => ({
															...d,
															holdPeriod: parseInt(e.target.value, 10) || 0,
														}))
														setIsDirty(true)
													}}
													className="h-6 w-14 text-xs text-right"
												/>
												<span className="text-[10px] text-gray-400">yrs</span>
											</label>
											<label className="flex items-center gap-1.5">
												<span className="text-gray-600">Exit cap rate</span>
												<div className="relative">
													<Input
														type="number"
														step={0.1}
														value={saleCapRatePct}
														onChange={e => {
															const pct = parseFloat(e.target.value)
															setDefaults(d => ({
																...d,
																saleCapRate: Number.isFinite(pct) ? pct / 100 : 0,
															}))
															setIsDirty(true)
														}}
														className="h-6 w-16 text-xs text-right pr-5"
													/>
													<span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">
														%
													</span>
												</div>
											</label>
											<label className="flex items-center gap-1.5">
												<span className="text-gray-600">Revenue growth</span>
												<div className="relative">
													<Input
														type="number"
														step={0.1}
														value={revenueGrowthRatePct}
														onChange={e => {
															const pct = parseFloat(e.target.value)
															setDefaults(d => ({
																...d,
																revenueGrowthRate: Number.isFinite(pct) ? pct / 100 : 0,
															}))
															setIsDirty(true)
														}}
														className="h-6 w-16 text-xs text-right pr-5"
													/>
													<span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">
														%
													</span>
												</div>
											</label>
										</div>
										{errors.defaults && (
											<p className="text-red-500 text-[0.7rem] mb-2">{errors.defaults}</p>
										)}

										{/* Line Items Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											<div className="grid grid-cols-[24px_32px_60px_1fr_140px_140px_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span className="text-center">Seq</span>
												<span className="text-center">Year</span>
												<span>Label</span>
												<span className="text-right pr-1">Amount</span>
												<span>Category</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox
														checked={isAllSelected}
														onCheckedChange={checked => handleSelectAll(!!checked)}
													/>
												</label>
												<span></span>
											</div>

											{itemsLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : itemOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No cash flows yet. Click "+ Add Line Item" to start a projection.
												</div>
											) : (
												<DndContext
													sensors={sensors}
													collisionDetection={closestCenter}
													onDragEnd={handleDragEnd}
												>
													<SortableContext
														items={itemOrder}
														strategy={verticalListSortingStrategy}
													>
														{itemOrder.map((id, index) => {
															const item = itemsMap[id]
															if (!item) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	item={item}
																	onYearChange={handleYearChange}
																	onLabelChange={handleLabelChange}
																	onCategoryChange={handleCategoryChange}
																	onAmountChange={handleAmountChange}
																	onDelete={handleDeleteItem}
																	isSelected={selectedItems.has(id)}
																	onSelect={handleSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.items && (
												<p className="text-red-500 text-[0.7rem] px-3 py-1">
													{errors.items}
												</p>
											)}
										</div>

										<p className="text-[11px] text-gray-400 mb-3">
											Year 0 holds the initial capital investment (negative amount).
											Years 1+ are cash flows; the final year typically includes a
											terminal value (exit sale).
										</p>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddItem}>
													<Plus className="w-4 h-4 mr-1" />
													Add Line Item
												</Button>
												{selectedItems.size > 0 && (
													<Button
														variant="ghost-red"
														onClick={handleDeleteSelected}
													>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedItems.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default ROIAnalysisTemplateCN
